import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import AspectRatio from 'components/AspectRatio';
import Carousel, { Dots } from 'components/Carousel';
import { NextCircleIcon, PrevCircleIcon } from 'components/Icon';
import Image from 'components/ImageV2';
import Modal from 'components/Modal';
import VideoPlayer from 'components/VideoPlayer';
import { IMAGE_BG_COLOR, IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { MEDIA_TYPES } from 'constants/media';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const PdpZoomView = class extends React.PureComponent {
  render() {
    const {
      isOpen,
      closeModal,
      modalSettings,
      slides,
      currentMainCarouselSlide,
      setPreviousMainCarouselSlide,
      setCurrentMainCarouselSlide,
      setNextMainCarouselSlide,
    } = this.props;

    return (
      <Modal
        isOpen={isOpen}
        className="PdpZoomView__modal"
        portalClassName="PdpZoomView__portal"
        onRequestClose={closeModal}
        {...modalSettings}
      >
        <div className="PdpZoomView__body">
          <div className="PdpZoomView__carousel">
            <AspectRatio className="PdpZoomView__aspect-ratio" width={1} height={1}>
              <Carousel
                className="PdpZoomView__main-carousel"
                currentSlide={currentMainCarouselSlide}
                onChange={setCurrentMainCarouselSlide}
                options={{ draggable: slides.length > 1 }}
              >
                {map(slides, ({ src, alt, poster, type }, i) =>
                  type === MEDIA_TYPES.IMAGE ? (
                    <div className="PdpZoomView__slide PdpZoomView__slide--image" key={i}>
                      <Image
                        src={src}
                        alt={alt}
                        className="PdpZoomView__slide-image"
                        transformations={{ backgroundColor: IMAGE_BG_COLOR }}
                        width={IMAGE_WIDTHS.W_3600}
                        ratio={IMAGE_RATIOS.R_1x1}
                      />
                    </div>
                  ) : (
                    <div className="PdpZoomView__slide PdpZoomView__slide--video" key={i}>
                      <VideoPlayer
                        url={src}
                        poster={poster}
                        playing={currentMainCarouselSlide === i}
                        loop
                        controls={false}
                        muted
                        aspectRatio={IMAGE_RATIOS.R_1x1}
                        playsinline
                      />
                    </div>
                  )
                )}
              </Carousel>
            </AspectRatio>
            <div className="PdpZoomView__dots">
              <Dots
                nDots={get(slides, 'length', 0)}
                currentDot={currentMainCarouselSlide}
                onClick={setCurrentMainCarouselSlide}
              />
            </div>

            <button
              className="PdpZoomView__nav PdpZoomView__nav--prev"
              onClick={setPreviousMainCarouselSlide}
              aria-label={I18n.localize('carousel.viewPreviousSlide')}
            >
              <PrevCircleIcon />
            </button>
            <button
              className="PdpZoomView__nav PdpZoomView__nav--next"
              onClick={setNextMainCarouselSlide}
              aria-label={I18n.localize('carousel.viewNextSlide')}
            >
              <NextCircleIcon />
            </button>
          </div>
        </div>
        <style jsx>
          {`
            .PdpZoomView__carousel {
              max-width: 95vmin;
              max-height: 95vmin;
              margin: 0 auto;
            }

            .PdpZoomView__dots {
              position: absolute;
              bottom: ${Theme.get('spacing') * 3}px;
              left: 50%;
              transform: translateX(-50%);
            }

            .PdpZoomView__nav {
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
              appearance: none;
              border: none;
              background-color: transparent;
              cursor: pointer;
            }

            .PdpZoomView__slide {
              text-align: center;
            }

            .PdpZoomView__nav--prev {
              left: ${Theme.get('spacing') * 3}px;
            }

            .PdpZoomView__nav--next {
              right: ${Theme.get('spacing') * 3}px;
            }
          `}
        </style>
        <style jsx global>
          {`
            .PdpZoomView__modal.Modal__content {
              padding: 0;
              width: 95vw;
              height: 95vmin;
              max-width: none;
              background-color: ${IMAGE_BG_COLOR};
            }
          `}
        </style>
      </Modal>
    );
  }
};

PdpZoomView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  modalSettings: PropTypes.shape({}),
  currentMainCarouselSlide: PropTypes.number.isRequired,
  setPreviousMainCarouselSlide: PropTypes.func.isRequired,
  setCurrentMainCarouselSlide: PropTypes.func.isRequired,
  setNextMainCarouselSlide: PropTypes.func.isRequired,
  slides: PropTypes.arrayOf(
    PropTypes.shape({ alt: PropTypes.string, src: PropTypes.string, isImage: PropTypes.bool })
  ).isRequired,
};

PdpZoomView.defaultProps = {
  modalSettings: {},
};

export default PdpZoomView;
